import { useCallback } from 'react';
import {
  atHomeMe,
  homeAction,
  homeBenefits,
  homeCaregivingDashboard,
  homeCognitive,
  homeMedications,
  homeReminders,
  homeResidentScore,
  homeEmergency,
  homePeople,
  homeToday,
  homeWellbeing,
  type HomeActionBody,
  type HomePeople,
  type HomeTone,
  type ResidentHome,
} from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useApi } from '../overview/common';

// The member's Home, loaded once for all five tabs (one route per tab, see
// packages/shared/src/api/home-routes.ts). GET /athome/me/:id is served; the rest are
// PROPOSED and answer 404 today, which each tab turns into its designed empty state.

export function useMemberData(personId: string | null, orgId: string | null) {
  const on = !!personId;
  const id = personId ?? '';
  const me = useApi((c, signal) => atHomeMe(c, { params: { id }, signal }), [id], on);
  const score = useApi((c, signal) => homeResidentScore(c, { params: { id }, signal }), [id], on);
  const cognitive = useApi((c, signal) => homeCognitive(c, { query: { userId: id }, signal }), [id], on);
  const meds = useApi((c, signal) => homeMedications(c, { query: { userId: id, organizationId: orgId ?? '' }, signal }), [id, orgId], on && !!orgId);
  const reminders = useApi((c, signal) => homeReminders(c, { query: { residentId: id }, signal }), [id], on);
  const dashboard = useApi((c, signal) => homeCaregivingDashboard(c, { query: { residentId: id }, signal }), [id], on);
  const today = useApi((c, signal) => homeToday(c, { params: { id }, signal }), [id], on);
  const wellbeing = useApi((c, signal) => homeWellbeing(c, { params: { id }, signal }), [id], on);
  const benefits = useApi((c, signal) => homeBenefits(c, { params: { id }, signal }), [id], on);
  const emergency = useApi((c, signal) => homeEmergency(c, { params: { id }, signal }), [id], on);
  const people = useApi((c, signal) => homePeople(c, { params: { id }, signal }), [id], on);
  return { me, score, cognitive, meds, reminders, dashboard, today, wellbeing, benefits, emergency, people };
}
export type MemberData = ReturnType<typeof useMemberData>;

/** Send one member action (PROPOSED POST /athome/me/:id/actions). Resolves the server's
 *  "done" sentence, or throws its error. */
export function useMemberAction(personId: string | null) {
  const { client } = useSession();
  return useCallback(
    async (body: HomeActionBody): Promise<string> => {
      if (!personId) throw new Error('No member');
      const r = await homeAction(client, { params: { id: personId }, body });
      return r.done;
    },
    [client, personId],
  );
}

export const TONE: Record<HomeTone, string> = { good: '#1F6B52', watch: '#4A5866', alert: '#B3261E', none: '#54606C' };

/** "42m ago", "10h 10m ago" (the design's ago). */
export function agoText(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h ? `${h}h ${m}m ago` : `${m}m ago`;
}

/** The first given name ("Ruth" of "Ruth Doyle"; "Dr Alvarez" stays whole). */
export function givenName(nm: string): string {
  const t = nm.split(' ')[0];
  return t.length > 2 && t.indexOf('.') < 0 && t !== 'Dr' ? t : nm;
}

/** The first family member in the care circle (who is called first), or null. */
export function firstPerson(p: HomePeople | null): HomePeople['people'][number] | null {
  return p?.people.find((x) => x.kind !== 'line') ?? null;
}

/** Who is called first, as the design words it ("Ruth", or "the Aithora care line"). */
export function callFirstOf(p: HomePeople | null, fallback: string | null = null): string {
  const f = firstPerson(p);
  if (f) return givenName(f.name);
  return fallback ? givenName(fallback) : 'the Aithora care line';
}

/** The care-team person (Katie R), or null. */
export function careWorkerOf(p: HomePeople | null): string | null {
  return p?.people.find((x) => x.kind === 'care')?.name ?? null;
}

/** A linked device's chip label (the design draws Tablet, Watch, Pendant). */
export function deviceLabel(provider: string): string {
  const p = provider.toLowerCase();
  if (/tablet|hub|companion/.test(p)) return 'Tablet';
  if (/pendant|bayalarm|bay_alarm|medicalguardian|medical_guardian|mgmini|mgmove/.test(p)) return 'Pendant';
  if (/oura|ring/.test(p)) return 'Ring';
  if (/sensor|motion|door/.test(p)) return 'Home sensors';
  if (/cuff|bp/.test(p)) return 'Blood pressure cuff';
  if (/scale/.test(p)) return 'Scale';
  if (/apple|watch|fitbit|garmin|withings|samsung|whoop/.test(p)) return 'Watch';
  return provider.charAt(0).toUpperCase() + provider.slice(1);
}

export const meFirstName = (me: ResidentHome | null) => (me ? me.name.split(' ')[0] : '');
